/**
 * Multi-Account Management Constants
 * Constants specific to multi-account functionality
 */

// Multi-Account Storage Keys
export const multiAccountKeys = {
  ACCOUNTS_LIST: 'smartmatrix_accounts_list',
  ACTIVE_ACCOUNT: 'smartmatrix_active_account',
  ACCOUNT_PREFIX: 'smartmatrix_account_',
  MIGRATION_STATUS: 'smartmatrix_migration_status'
} as const;

// Multi-Account Configuration
export const multiAccountConfig = {
  // Maximum number of accounts a user can have
  MAX_ACCOUNTS: 10,
  
  // Account validation settings
  VALIDATION_INTERVAL_MS: 10 * 60 * 1000, // 10 minutes
  
  // Account switching settings
  SWITCH_TIMEOUT_MS: 5000, // 5 seconds
  
  // Encryption settings
  ENCRYPTION_VERSION: '1.0',
  SESSION_KEY_ROTATION_HOURS: 24, // Rotate session key every 24 hours
  
  // UI settings
  PROFILE_PICTURE_SIZE: 40, // pixels
  ACCOUNT_SWITCHER_MAX_HEIGHT: 400, // pixels
  
  // Migration settings
  MIGRATION_VERSION: '1.0',
  BACKUP_SINGLE_ACCOUNT: true
} as const;

// Account Switcher UI Constants
export const accountSwitcherUi = {
  ANIMATION_DURATION: 200, // milliseconds
  HOVER_DELAY: 100, // milliseconds
  DROPDOWN_OFFSET: 8, // pixels
  
  // Colors (Tailwind classes)
  ACTIVE_ACCOUNT_BG: 'bg-blue-50',
  HOVER_BG: 'hover:bg-gray-50',
  BORDER_COLOR: 'border-gray-200',
  
  // Typography
  ACCOUNT_NAME_SIZE: 'text-sm font-medium',
  EMAIL_SIZE: 'text-xs text-gray-500',
  ACTION_SIZE: 'text-sm'
} as const;

// Error Messages
export const multiAccountErrors = {
  ENCRYPTION_NOT_SUPPORTED: 'Encryption not supported in this browser',
  ACCOUNT_NOT_FOUND: 'Account not found',
  DUPLICATE_ACCOUNT: 'An account with this email already exists',
  MAX_ACCOUNTS_REACHED: 'Maximum number of accounts reached',
  INVALID_ACCOUNT_DATA: 'Invalid account data',
  STORAGE_ERROR: 'Failed to access storage',
  DECRYPTION_ERROR: 'Failed to decrypt account data',
  NETWORK_ERROR: 'Network error during account operation',
  SESSION_EXPIRED: 'Account session has expired'
} as const;

// Success Messages
export const multiAccountMessages = {
  ACCOUNT_ADDED: 'Account added successfully',
  ACCOUNT_REMOVED: 'Account removed successfully',
  ACCOUNT_SWITCHED: 'Switched to account',
  ACCOUNTS_CLEARED: 'All accounts cleared',
  MIGRATION_COMPLETE: 'Account migration completed'
} as const;

// Account Status Types
export type AccountStatus = 'active' | 'inactive' | 'expired' | 'invalid';

// Account Action Types
export type AccountAction = 'add' | 'remove' | 'switch' | 'refresh' | 'validate';

// Storage Migration Types
export interface MigrationStatus {
  version: string;
  migrated: boolean;
  migratedAt?: number;
  backupCreated?: boolean;
  migratedAccountId?: string;
  migrationFailed?: boolean;
  failureReason?: string;
  migrationSkipped?: boolean;
  skipReason?: string;
  rolledBack?: boolean;
}

// Account Validation Result
export interface AccountValidationResult {
  accountId: string;
  isValid: boolean;
  status: AccountStatus;
  error?: string;
  requiresReauth?: boolean;
}